import {
    ADDRESS_MODE,
    isRecognisedStreetType,
    toAddressFormat,
} from './to-address-format';

export interface BuildingNameFormatInput {
    readonly name: string;
    readonly address: string;
}

export function toBuildingNameFormat(value: BuildingNameFormatInput): string {
    const name = value.name.trim();
    const fullAddress = toAddressFormat(value.address, ADDRESS_MODE.Full);
    const streetAddress = toAddressFormat(value.address, ADDRESS_MODE.Street);

    if (!name) {
        return streetAddress;
    }

    // Default building names historically duplicated the full address. Prefer
    // the current street value while both sources still agree exactly.
    if (normaliseForComparison(name) === normaliseForComparison(fullAddress)) {
        return streetAddress;
    }

    if (
        normaliseForComparison(name) === normaliseForComparison(streetAddress)
    ) {
        return streetAddress;
    }

    // Some legacy address strings lost their street type after the default name
    // was generated. An exact city suffix match lets us retain the richer street
    // value from the name without rewriting unrelated custom building names.
    const nameParts = name.split(',');
    const streetAddressFromName = nameParts.shift()?.trim() ?? '';
    const cityAddressFromName = nameParts.join(' ');
    const cityAddress = value.address.split(',').slice(1).join(' ');

    if (
        cityAddress &&
        hasCompatibleStreetAddress(streetAddressFromName, streetAddress) &&
        normaliseForComparison(cityAddressFromName) ===
            normaliseForComparison(cityAddress)
    ) {
        return toAddressFormat(streetAddressFromName, ADDRESS_MODE.Street);
    }

    // A non-address name is user-authored identity, so its stored spelling is
    // authoritative rather than another address-like string to normalise.
    return name;
}

function hasCompatibleStreetAddress(
    streetAddressFromName: string,
    streetAddress: string
): boolean {
    const normalisedNameStreet = normaliseForComparison(streetAddressFromName);
    const normalisedAddressStreet = normaliseForComparison(streetAddress);

    if (normalisedNameStreet === normalisedAddressStreet) {
        return true;
    }

    const streetTypePrefix = `${normalisedAddressStreet} `;
    if (!normalisedNameStreet.startsWith(streetTypePrefix)) {
        return false;
    }

    const streetType = normalisedNameStreet.slice(streetTypePrefix.length);
    return isRecognisedStreetType(streetType);
}

function normaliseForComparison(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .replace(/\p{P}+/gu, ' ')
        .replace(/\s+/g, ' ')
        .trim();
}
